// src/core/slideshow-state.js
//
// SlideshowState
// ---------------
// Reactive state container (EventTarget).
// Holds:
//   - media[] (images + videos)
//   - index
//   - shuffle
//   - includeSubdirs
//   - currentLayoutId
//   - transitionTime (ms)
import { SettingsStore } from "core/settings-store.js";
import { LayoutManager } from "core/layout-manager";

function shuffleNew(array) {
  const result = array.slice(); // clone
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export class SlideshowState extends EventTarget {
  constructor() {
    super();

    this.media = [];
    this.index = 0;

    this.settings = new SettingsStore();
    this.layoutManager = new LayoutManager();

    this.fit = this.settings.fit;
    this.includeSubdirs = this.settings.includeSubdirs;

    this.currentLayoutId = this.settings.layoutId;
    this.transitionTime = this.settings.intervalSeconds || 3; // default autoplay + transition time
  }

  _emit() {
    this.dispatchEvent(new Event("change"), { bubbles: true, composed: true });
  }

  // ------------------------------------------------------------
  // Media
  // ------------------------------------------------------------
  setMedia(media) {
    this.media = media || [];
    this.index = 0;
    this._emit();
  }

  // ------------------------------------------------------------
  // Index
  // ------------------------------------------------------------
  setIndex(i) {
    if (!this.media.length) return;
    this.index = Math.max(0, Math.min(i, this.media.length - 1));
    this._emit();
  }

  next() {
    const step = this.getCurrentSliceSize();
    this.index = (this.index + step) % this.media.length;
    this._emit();
  }

  prev() {
    const step = this.getCurrentSliceSize();
    this.index = (this.index - step + this.media.length) % this.media.length;
    this._emit();
  }

  // ------------------------------------------------------------
  // Flags
  // ------------------------------------------------------------
  shuffleMedia() {
    this.media = shuffleNew(this.media);
    this._emit();
  }

  toggleIncludeSubdirs() {
    this.includeSubdirs = !this.includeSubdirs;
    this.settings.includeSubdirs = this.includeSubdirs;
    this._emit();
  }

  toggleFit() {
    this.fit = !this.fit;
    this._emit();
  }

  // ------------------------------------------------------------
  // Layout + timing
  // ------------------------------------------------------------
  setCurrentLayoutId(id) {
    this.currentLayoutId = id;
    this.settings.layoutId = id;
    this._emit();
  }

  setTransitionTime(seconds) {
    this.transitionTime = seconds;
    this.settings.intervalSeconds = seconds;
    this._emit();
  }

  // ------------------------------------------------------------
  // Slice selection
  // ------------------------------------------------------------
  _countForLayout(layoutId) {
    return this.layoutManager.getNumSlots(layoutId);
  }

  getSlice() {
    if (!this.media.length) return [];

    const layoutId = this.currentLayoutId || "single";
    const count = this._countForLayout(layoutId);

    const out = [];
    for (let i = 0; i < count; i++) {
      const idx = (this.index + i) % this.media.length;
      out.push(this.media[idx]);
    }

    return out;
  }

  getCurrentSliceSize() {
    return this._countForLayout(this.currentLayoutId);
  }
}
